/**
 * Test Webhook Result
 *
 * Inline display for webhook test results.
 * Per Design 5.1: Success shows status code + response time, failure shows code + reason.
 */

import { CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import type { WebhookTestResult as TestResult } from '@/types/webhook-config';

interface TestWebhookResultProps {
  result: TestResult;
}

export function TestWebhookResult({ result }: TestWebhookResultProps) {
  const { t } = useTranslation('webhook-config');
  const isSuccess = result.testResult === 'success';

  return (
    <div
      className={`flex items-start gap-3 rounded-md border p-3 ${
        isSuccess
          ? 'border-green-500/30 bg-green-500/10'
          : 'border-red-500/30 bg-red-500/10'
      }`}
    >
      {isSuccess ? (
        <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
      ) : (
        <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
      )}
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {isSuccess ? t('test.success') : t('test.failed')}
        </p>
        {isSuccess && (
          <div className="flex gap-2">
            {result.responseStatusCode && (
              <Badge variant="outline" className="text-xs">
                {t('test.statusCode', { code: result.responseStatusCode })}
              </Badge>
            )}
            {result.responseTimeMs !== undefined && (
              <Badge variant="outline" className="text-xs">
                {t('test.responseTime', { ms: result.responseTimeMs })}
              </Badge>
            )}
          </div>
        )}
        {!isSuccess && (
          <div className="space-y-0.5 text-sm text-muted-foreground">
            {result.failureCode && (
              <p>{t('test.failureCode', { code: result.failureCode })}</p>
            )}
            {result.failureReason && (
              <p>{t('test.failureReason', { reason: result.failureReason })}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
